import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
} from '@angular/core';

import { TrackerComponent } from '@components/tracker/tracker.component';
import {
  TabMenuComponent,
  ExpandablePanelComponent,
  TabItem,
} from '@tiltgroup/web-components';
import { ActivatedRoute, Router } from '@angular/router';
import { DisplayLoad } from '@services/shipment.model';
import { TrackerService } from '@services/tracker.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, switchMap, tap } from 'rxjs';
import { ShipmentInfoComponent } from '@components/shipment-info/shipment-info.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { LastUpdatedTextPipe } from '@shared/pipes/last-updated-text.pipe';

@Component({
  selector: 'lt-track',
  imports: [
    TrackerComponent,
    TabMenuComponent,
    ShipmentInfoComponent,
    MatProgressSpinner,
    ExpandablePanelComponent,
    LastUpdatedTextPipe,
  ],
  templateUrl: './track.component.html',
  styleUrls: ['./track.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TrackComponent {
  private router = inject(Router);
  private trackerService = inject(TrackerService);
  private activatedRoute = inject(ActivatedRoute);

  trackingNumbers = signal<string[] | null>(
    this.activatedRoute.snapshot.queryParams.trackingNumber ?? null
  );

  customerReference = signal<string | null>(
    this.activatedRoute.snapshot.queryParams.customerRef ?? null
  );

  selectedStatus = signal<string>('All');

  trackedShipments = signal<DisplayLoad[]>([]);
  filteredShipments = computed(() => {
    return this.trackedShipments().filter((shipment) => {
      return (
        shipment.displayStatus === this.selectedStatus() ||
        this.selectedStatus() === 'All'
      );
    });
  });

  private baseMenuItem: TabItem = {
    title: 'All',
    clickFn: (i: TabItem) => {
      this.filterShipments(i.title);
    },
  };

  trackFilters: Signal<TabItem[]> = computed(() => {
    return [
      {
        ...this.baseMenuItem,
        active: true,
        badgeNumber: this.trackedShipments().length,
      },
      {
        ...this.baseMenuItem,
        title: 'Booked',
        badgeNumber: this.getFilteredShipments(
          this.trackedShipments(),
          'Booked'
        ).length,
      },
      {
        ...this.baseMenuItem,
        title: 'Picked up',
        badgeNumber: this.getFilteredShipments(
          this.trackedShipments(),
          'Picked up'
        ).length,
      },
      {
        ...this.baseMenuItem,
        title: 'In transit',
        badgeNumber: this.getFilteredShipments(
          this.trackedShipments(),
          'In transit'
        ).length,
      },
      {
        ...this.baseMenuItem,
        title: 'Delivered',
        badgeNumber: this.getFilteredShipments(
          this.trackedShipments(),
          'Delivered'
        ).length,
      },
    ];
  });

  busy = signal(false);

  constructor() {
    effect(() => {
      const numbers = this.trackingNumbers();
      const customerRef = this.customerReference();
      if (numbers !== null && numbers.length > 0) {
        this.router.navigate(['track'], {
          queryParams: {
            trackingNumber: numbers,
            ...(customerRef && { customerRef }),
          },
        });
      }
    });

    this.subscribeToQueryParams();
  }

  private subscribeToQueryParams() {
    this.activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(),
        map((params) => {
          return {
            ...params,
            trackingNumber: Array.isArray(params.trackingNumber)
              ? params.trackingNumber
              : params.trackingNumber
                ? [params.trackingNumber]
                : null,
            customerRef: params.customerRef ?? null,
          };
        }),
        filter(
          (params) =>
            params.trackingNumber !== null && params.trackingNumber.length > 0
        ),
        tap((params) => {
          this.trackingNumbers.set(params.trackingNumber);
          this.customerReference.set(params.customerRef);
          this.busy.set(true);
        }),
        switchMap((params) =>
          this.trackerService.getShipments(
            params.trackingNumber,
            params.customerRef
          )
        )
      )
      .subscribe({
        next: (shipments) => {
          this.trackedShipments.set(shipments);
          this.busy.set(false);
        },
        error: () => this.busy.set(false),
        complete: () => this.busy.set(false),
      });
  }

  private filterShipments(status: string) {
    this.selectedStatus.set(status);
  }

  private getFilteredShipments(
    shipments: DisplayLoad[],
    status: string
  ): DisplayLoad[] {
    return shipments.filter((s) => s.displayStatus === status);
  }
}
